import { useMemo, useReducer } from 'react';
import type { PerformanceResultType } from '@/apolloClient.types';

export enum BulkActionEnum {
    Import = 'import',
    Export = 'export',
}

export type BulkAction = BulkActionEnum.Import | BulkActionEnum.Export | null;

export enum BulkStepEnum {
    ChooseAction = 'choose-action',
    ImportSelect = 'import-select',
    Validate = 'validate',
}

export type BulkStep = BulkStepEnum.ChooseAction | BulkStepEnum.ImportSelect | BulkStepEnum.Validate;

export interface ValidationErrorRow {
    row: number;
    field: string;
    description: string;
}

export interface ValidationSummary {
    readyCount: number;
    errorCount: number;
    errors: ValidationErrorRow[];
}

export interface BulkState {
    step: BulkStep;
    action: BulkAction;
    resultType: PerformanceResultType;
    isSubmitting: boolean;
    isProcessing: boolean;
    file?: File | null;
    validation?: ValidationSummary;
    notificationMessage?: string | React.ReactNode;
}

type BulkActionType =
    | { type: 'CHOOSE_ACTION'; payload: BulkAction }
    | { type: 'SET_STEP'; payload: BulkStep }
    | { type: 'NEXT' }
    | { type: 'BACK' }
    | { type: 'RESET' }
    | { type: 'SET_FILE'; payload?: File | null }
    | { type: 'SUBMIT_START' }
    | { type: 'SUBMIT_END' }
    | { type: 'SET_IS_PROCESSING_START' }
    | { type: 'SET_IS_PROCESSING_END' }
    | { type: 'SET_VALIDATION'; payload: Partial<ValidationSummary> }
    | { type: 'SET_NOTIFICATION_MESSAGE'; payload: string | React.ReactNode };

const stepAfterChoose = (action: BulkAction): BulkStep => {
    if (action === BulkActionEnum.Import) {
        return BulkStepEnum.ImportSelect;
    }
    if (action === BulkActionEnum.Export) {
        return BulkStepEnum.ChooseAction;
    } // stay on the same step for export
    return BulkStepEnum.ChooseAction;
};

function reducer(state: BulkState, action: BulkActionType): BulkState {
    switch (action.type) {
        case 'CHOOSE_ACTION':
            return {
                ...state,
                action: action.payload,
                step: stepAfterChoose(action.payload),
                notificationMessage: undefined,
            };
        case 'SET_STEP':
            return { ...state, step: action.payload };
        case 'NEXT': {
            if (state.step === BulkStepEnum.ImportSelect) {
                return { ...state, step: BulkStepEnum.Validate };
            }
            return state;
        }
        case 'BACK': {
            if (state.step === BulkStepEnum.ImportSelect) {
                return { ...state, step: BulkStepEnum.ChooseAction, action: null };
            }
            if (state.step === BulkStepEnum.Validate) {
                return { ...state, step: BulkStepEnum.ImportSelect };
            }
            return state;
        }
        case 'RESET':
            return {
                ...state,
                step: BulkStepEnum.ImportSelect,
                action: BulkActionEnum.Import,
                file: undefined,
                validation: undefined,
            };
        case 'SET_FILE':
            return { ...state, file: action.payload };
        case 'SUBMIT_START':
            return { ...state, isSubmitting: true };
        case 'SUBMIT_END':
            return { ...state, isSubmitting: false };
        case 'SET_VALIDATION':
            return {
                ...state,
                validation: state.validation
                    ? { ...state.validation, ...action.payload }
                    : (action.payload as ValidationSummary),
            };
        case 'SET_IS_PROCESSING_START':
            return { ...state, isProcessing: true };
        case 'SET_IS_PROCESSING_END':
            return { ...state, isProcessing: false };
        case 'SET_NOTIFICATION_MESSAGE':
            return { ...state, notificationMessage: action.payload };
        default:
            return state;
    }
}

export interface BulkActions {
    chooseAction: (action: BulkAction) => void;
    setStep: (step: BulkStep) => void;
    next: () => void;
    back: () => void;
    reset: () => void;
    setFile: (file?: File | null) => void;
    submitStart: () => void;
    submitEnd: () => void;
    setValidation: (partial: Partial<ValidationSummary>) => void;
    setIsProcessingStart: () => void;
    setIsProcessingEnd: () => void;
    setNotificationMessage: (message?: string | React.ReactNode) => void;
}

export const createInitialBulkState = (resultType: PerformanceResultType): BulkState => ({
    step: BulkStepEnum.ImportSelect,
    action: BulkActionEnum.Import,
    isSubmitting: false,
    isProcessing: false,
    resultType,
});

export const useBulkModificationState = (resultType: PerformanceResultType) => {
    const [state, dispatch] = useReducer(reducer, createInitialBulkState(resultType));

    const actions = useMemo<BulkActions>(
        () => ({
            chooseAction: a => dispatch({ type: 'CHOOSE_ACTION', payload: a }),
            setStep: s => dispatch({ type: 'SET_STEP', payload: s }),
            next: () => dispatch({ type: 'NEXT' }),
            back: () => dispatch({ type: 'BACK' }),
            reset: () => dispatch({ type: 'RESET' }),
            setFile: f => dispatch({ type: 'SET_FILE', payload: f }),
            submitStart: () => dispatch({ type: 'SUBMIT_START' }),
            submitEnd: () => dispatch({ type: 'SUBMIT_END' }),
            setValidation: s => dispatch({ type: 'SET_VALIDATION', payload: s }),
            setIsProcessingStart: () => dispatch({ type: 'SET_IS_PROCESSING_START' }),
            setIsProcessingEnd: () => dispatch({ type: 'SET_IS_PROCESSING_END' }),
            setNotificationMessage: message => dispatch({ type: 'SET_NOTIFICATION_MESSAGE', payload: message }),
        }),
        []
    );

    const selectors = useMemo(() => {
        const hasFile = !!state.file;
        return { hasFile };
    }, [state.file]);

    return { state, actions, selectors };
};

export default useBulkModificationState;
